import { AnalyticsService } from '@alison/analytics/client';
import { FillBar } from '@alison/ui';
import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { MediaCard } from '../../../media/shared/MediaCard.js';
import type {
  CreativeScore,
  CreativeScoreComponentName,
  ScoreTier,
} from '../../data/creativeScore.js';
import {
  creativeScoreComponentLabel,
  orderedScoreComponents,
  scoreTier,
} from '../../data/creativeScore.js';
import { CREATIVE_SCORE_COMPONENT_ACCENT } from '../../data/presentation.js';
import { accentColor } from '../../MetricTile.js';
import { ScoreGauge } from '../shared/ScoreGauge.js';

const TIER_LABEL: Record<ScoreTier, string> = {
  low: 'Low',
  medium: 'Average',
  high: 'High',
};

interface ScoreBreakdownCardProps {
  score: number | null;
  previous: number | null;
  components: CreativeScore['components'];
  leadingKpiLabel: string;
  selectedComponents?: CreativeScoreComponentName[];
  onComponentSelect?: (name: CreativeScoreComponentName) => void;
}

export function ScoreBreakdownCard({
  score,
  previous,
  components,
  leadingKpiLabel,
  selectedComponents = [],
  onComponentSelect,
}: ScoreBreakdownCardProps): ReactNode {
  const theme = useTheme();
  const tier = score === null ? null : scoreTier(score);
  const componentLabels = creativeScoreComponentLabel(leadingKpiLabel);

  return (
    <MediaCard sx={{ p: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
          Creative Score
        </Typography>
        {tier !== null && (
          <Box
            sx={(theme) => ({
              px: 1.5,
              py: 1,
              borderRadius: `${theme.radius.md}px`,
              bgcolor: theme.palette.score[tier].bg,
              color: theme.palette.score[tier].main,
            })}
          >
            <Typography
              sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: 'inherit' }}
            >
              {TIER_LABEL[tier]}
            </Typography>
          </Box>
        )}
      </Box>
      <Typography sx={{ color: 'text.secondary', mt: 1 }}>
        Scored against your account's winning patterns across every element of this creative.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 6, mt: 6 }}>
        <ScoreGauge score={score} previous={previous} size={160} />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {orderedScoreComponents(components).map(([name, component]) => (
            <Box
              key={name}
              {...(onComponentSelect === undefined
                ? {}
                : {
                    component: 'button',
                    type: 'button',
                    'aria-pressed': selectedComponents.includes(name),
                    onClick: () => {
                      AnalyticsService.track('Creative Detail - Select Score Component', {
                        component: name,
                      });
                      onComponentSelect(name);
                    },
                  })}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                width: '100%',
                border: 0,
                px: 2,
                py: 1,
                mx: -2,
                borderRadius: `${theme.radius.md}px`,
                font: 'inherit',
                textAlign: 'left',
                cursor: onComponentSelect === undefined ? 'default' : 'pointer',
                bgcolor: selectedComponents.includes(name)
                  ? alpha(accentColor(theme, CREATIVE_SCORE_COMPONENT_ACCENT[name]), 0.1)
                  : 'transparent',
                '&:hover': {
                  bgcolor:
                    onComponentSelect === undefined
                      ? undefined
                      : alpha(accentColor(theme, CREATIVE_SCORE_COMPONENT_ACCENT[name]), 0.06),
                },
              })}
            >
              <Box sx={{ width: 100 }}>
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                  }}
                >
                  {componentLabels[name]}
                </Typography>
              </Box>
              <FillBar
                color={accentColor(theme, CREATIVE_SCORE_COMPONENT_ACCENT[name])}
                percent={component.score}
                label={componentLabels[name]}
                sx={{ flex: 1 }}
              />
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'text.primary',
                  width: 28,
                  textAlign: 'right',
                }}
              >
                {component.score === null ? '—' : Math.round(component.score)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </MediaCard>
  );
}
